// client/src/pages/FlightRhythmReporting.js
//
// Client-authorized Flight Rhythm reporting.
//
// The Flight Rhythm client panel (components/FlightRhythmPanel.js) was first
// mounted inside pages/CreativeExecution.js, but /creative-executions is a
// protected Beta surface (CREATIVE_BETA_INTERNAL_ACCESS): every Cortex
// principal is redirected by BetaSurfaceRoute and the page component is not
// imported into the Cortex bundle at all. That left the panel unreachable for
// the clients it reports to.
//
// This page is the authorized path. It lifts ONLY the Rhythm-related mounting
// from Creative Execution — the governed advertiser selector and the panel —
// and none of the Beta governance content. The beta restriction itself is
// untouched: this file must never import pages/CreativeExecution.js.
//
// Data: GET /api/flight-rhythm/:advertiserId/campaigns and
//       GET /api/flight-rhythm/:advertiserId/campaigns/:campaignId
// Both are advertiser-scoped on the server (404 on a foreign advertiser); the
// panel surfaces that as its own alert and ignores late responses for a
// previously selected scope.

import React from "react";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";
import "./SherlocMediaCampaigns.css";
import "./FlightRhythmReporting.css";
import FlightRhythmPanel from "../components/FlightRhythmPanel";
import CphereHeaderLogo from "../components/CphereHeaderLogo";
import {
  AdvertiserIdentityTrigger,
  GovernedAdvertiserSelectorModal,
} from "../components/GovernedAdvertiserSelector";
import useGovernedAdvertiserCatalog from "../hooks/useGovernedAdvertiserCatalog";

const normalizeBase = (base) => {
  if (!base) return "";
  let next = String(base).trim();
  next = next.replace(/\/+$/, "");
  if (next.toLowerCase().endsWith("/api")) next = next.slice(0, -4);
  return next;
};

const API_BASE_URL =
  normalizeBase(process.env.REACT_APP_BACKEND_URL) ||
  normalizeBase(process.env.REACT_APP_BACKEND) ||
  normalizeBase(process.env.REACT_APP_API_URL) ||
  "http://localhost:5000";

// Same token precedence as ProtectedRouteClient: this is a client-only page.
function getAuthToken() {
  try {
    return (
      localStorage.getItem("clientAuthToken") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("token") ||
      ""
    );
  } catch {
    return "";
  }
}

// Module-level so the reference is stable: the panel's effects depend on it.
async function apiJson(path, options = {}) {
  const token = getAuthToken();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    signal: options.signal,
    method: options.method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
    credentials: "include",
  });
  const json = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(json?.error || json?.detail || `Request failed (${response.status})`);
  }
  return json;
}

const FlightRhythmReporting = () => {
  const navigate = useNavigate();

  const {
    selectedAdvertiserId,
    selectedAdvertiserName,
    visibleAdvertisers: advertisers,
    currentAdvertiser,
    loading: advertisersLoading,
    error: advertiserCatalogError,
    errorMessage: advertiserCatalogMessage,
    selectorDisabled,
    selectorEmpty,
    modalOpen: advertiserSelectorOpen,
    openSelector: openAdvertiserSelector,
    closeSelector: closeAdvertiserSelector,
    handleAdvertiserSelect,
  } = useGovernedAdvertiserCatalog({
    apiBase: API_BASE_URL,
    role: "client",
  });

  return (
    <div className="smc-page-container flight-rhythm-reporting-page" data-testid="flight-rhythm-reporting-page">
      <div className="smc-page-content">
        <CphereHeaderLogo
          alt="Cphere"
          className="smc-poweredby-logo"
          onClick={() => navigate("/dashboard")}
          style={{ cursor: "pointer" }}
        />

        <div className="cphere-governed-hero-selector-stack">
          <div className="smc-hero-container">
            <div
              className="smc-hero-pill"
              role="heading"
              aria-level="1"
              aria-label="Flight Rhythm Reporting"
            >
              <span className="smc-hero-pill-label cphere-hero-pill-wordmark">Flight</span>
              <span className="smc-hero-pill-tag cphere-hero-pill-tag">Rhythm</span>
            </div>
            <div className="smc-hero-shimmer-bar" />
          </div>

          <div className="cphere-governed-advertiser-selector-stage">
            <AdvertiserIdentityTrigger
              advertiser={currentAdvertiser}
              advertiserName={selectedAdvertiserName}
              apiBase={API_BASE_URL}
              disabled={selectorDisabled}
              empty={selectorEmpty}
              loading={advertisersLoading}
              modalOpen={advertiserSelectorOpen}
              onClick={openAdvertiserSelector}
            />
          </div>
        </div>

        {advertiserSelectorOpen ? (
          <GovernedAdvertiserSelectorModal
            advertisers={advertisers}
            selectedAdvertiserId={selectedAdvertiserId}
            selectedAdvertiserName={selectedAdvertiserName}
            apiBase={API_BASE_URL}
            onSelect={handleAdvertiserSelect}
            onClose={closeAdvertiserSelector}
          />
        ) : null}

        {advertiserCatalogError ? (
          <div className="mediacampaigns-error" role="alert">
            {advertiserCatalogMessage || advertiserCatalogError}
          </div>
        ) : null}

        <FlightRhythmPanel advertiserId={selectedAdvertiserId} apiJson={apiJson} />
      </div>
    </div>
  );
};

export default FlightRhythmReporting;
